/**
 * Situational Awareness Layer — Helper #3: Decision Clarity (Phase 3)
 * ───────────────────────────────────────────────────────────────────
 * Pure presentation helpers that make Push Mode resolutions feel
 * intentional, urgent-aware, and trust-building. Three primitives:
 *
 *   • getDecisionHeader(scenario) → what decision the operator is making
 *   • getRiskSignal(issue, job, openIssueCount) → stakes / urgency
 *   • getOutcomeSummary(payload)  → operator-facing confirmation prose
 *
 * RULE (mem://architecture/situational-awareness-layer):
 * Do not inline scenario-specific decision/risk/outcome strings in the
 * modal. Extend these helpers instead.
 */
import type { Issue, Job } from "../types";
import type { IssueScenario, ResolutionPayload } from "./requiredInputs";

// ────────────────────────────────────────────────────────────────────
// Decision header
// ────────────────────────────────────────────────────────────────────

export interface DecisionHeader {
  title: string;
  description: string;
}

const DECISION_HEADERS: Record<IssueScenario, DecisionHeader> = {
  material: {
    title: "Confirm material resolution and customer communication",
    description: "Lock in the sourcing action so the install can move forward.",
  },
  access: {
    title: "Confirm access resolution and next visit readiness",
    description: "Capture what's blocking entry and whether we're cleared to return.",
  },
  scheduling: {
    title: "Confirm scheduling resolution and customer alignment",
    description: "Lock in the date, crew, and customer confirmation so the visit can proceed.",
  },
  service: {
    title: "Confirm service handling and homeowner follow-up",
    description: "Record how the service was handled and what the homeowner expects next.",
  },
  permit: {
    title: "Confirm permit status and authority follow-up",
    description: "Update the permit stage and any municipality follow-up that's owed.",
  },
  generic: {
    title: "Resolve this issue",
    description: "Capture the resolution so the job can move forward.",
  },
};

export function getDecisionHeader(scenario: IssueScenario): DecisionHeader {
  return DECISION_HEADERS[scenario] ?? DECISION_HEADERS.generic;
}

// ────────────────────────────────────────────────────────────────────
// Risk / urgency signal
// ────────────────────────────────────────────────────────────────────

export type RiskTone = "high" | "medium" | "low" | "clear";

export interface RiskSignal {
  tone: RiskTone;
  message: string;
}

const DAY_MS = 1000 * 60 * 60 * 24;

/** Days since the issue was created — observable, no new computation. */
function daysSince(iso?: string): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / DAY_MS));
}

/**
 * Derive a risk tone from already-available context. Inputs are intentionally
 * cheap: severity, days stale, active blocker count. No new selectors.
 *
 * Thresholds:
 *   • high   — Urgent severity OR ≥4 days since issue opened
 *   • medium — ≥2 active blockers OR ≥2 days stale
 *   • clear  — no active blockers (post-resolution / nothing else open)
 *   • low    — otherwise
 */
export function getRiskSignal(
  issue: Issue | null | undefined,
  job: Job | undefined,
  openIssueCount: number,
): RiskSignal {
  if (!issue) {
    return { tone: "low", message: "Standing by." };
  }

  const stale = daysSince(issue.dateCreated);
  const urgent = issue.severity === "Urgent";

  if (urgent || stale >= 4) {
    // Severity takes priority over staleness (matches existing precedence).
    const message = urgent
      ? "Urgent issue — requires immediate attention"
      : `Customer waiting ${stale} days — action required now`;
    return { tone: "high", message };
  }

  if (openIssueCount >= 2 || stale >= 2) {
    const message =
      openIssueCount >= 2
        ? "Job cannot progress — blocker requires resolution"
        : `Customer waiting ${stale} days — follow-up due soon`;
    return { tone: "medium", message };
  }

  if (openIssueCount === 0) {
    return { tone: "clear", message: "No active blockers — ready for next step" };
  }

  return {
    tone: "low",
    message: job?.scheduledInstallDate
      ? "On track — no escalation signals"
      : "No escalation signals",
  };
}

// ────────────────────────────────────────────────────────────────────
// Outcome summary — operator-facing confirmation prose
// ────────────────────────────────────────────────────────────────────

/** Format a select value back to its label using the scenario's known options. */
function selectLabel(payload: ResolutionPayload, key: string, fallback?: string): string | undefined {
  const v = payload.fields[key];
  if (typeof v !== "string" || !v) return fallback;
  // Map known values back to short readable labels per scenario.
  const map: Record<string, string> = {
    // material
    ordered_replacement: "Replacement ordered",
    reordered_missing: "Missing item reordered",
    verified_inventory: "Inventory verified",
    waiting_vendor: "Waiting on vendor",
    // access
    locked: "Locked / unavailable",
    hoa_gate: "HOA / gate restriction",
    customer_unavailable: "Customer unavailable",
    obstruction: "Pet / safety obstruction",
    // service
    phone_guidance: "Resolved by phone",
    service_visit: "Service visit needed",
    subcontractor: "Crew follow-up",
    parts_required: "Parts required",
    // permit
    submitted: "Submitted",
    pending_municipality: "Pending municipality",
    rfi: "RFI in motion",
    approved: "Approved",
    rejected: "Rejected",
  };
  return map[v] ?? fallback ?? v;
}

function fmtDate(value: string | boolean | undefined): string | undefined {
  if (typeof value !== "string" || !value) return undefined;
  try {
    return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return value;
  }
}

/**
 * Operator-facing summary, distinct from the audit-grade
 * `buildResolutionPayload.summary`. Shorter, warmer, action-confirming.
 */
export function getOutcomeSummary(payload: ResolutionPayload | null | undefined): string {
  if (!payload) return "Resolution recorded.";
  const f = payload.fields;

  switch (payload.scenario) {
    case "material": {
      const action = selectLabel(payload, "actionTaken", "Material action recorded");
      const vendor = typeof f.vendor === "string" && f.vendor ? ` from ${f.vendor}` : "";
      const eta = fmtDate(f.eta);
      const etaPart = eta ? `, ETA ${eta}` : "";
      const notified = f.customerNotified === true ? " — customer notified." : f.customerNotified === false ? " — customer not yet notified." : ".";
      return `${action}${vendor}${etaPart}${notified}`;
    }
    case "access": {
      const problem = selectLabel(payload, "accessProblem", "Access issue logged");
      const contacted = f.customerContacted === true ? "customer contacted" : "customer not yet contacted";
      const followUp = f.followUpRequired === true ? "follow-up required" : "no follow-up needed";
      const rescheduled = f.visitRescheduled === true ? "visit rescheduled" : null;
      const tail = [contacted, followUp, rescheduled].filter(Boolean).join(", ");
      return `${problem} — ${tail}.`;
    }
    case "service": {
      const handled = selectLabel(payload, "handledBy", "Service handled");
      const contacted = f.homeownerContacted === true ? "homeowner contacted" : "homeowner not yet contacted";
      const visit = f.scheduleServiceCall === true ? "service call scheduled" : null;
      const tail = [contacted, visit].filter(Boolean).join(", ");
      return `${handled} — ${tail}.`;
    }
    case "permit": {
      const status = selectLabel(payload, "permitStatus", "Permit updated");
      const auth = f.authorityContactNeeded === true ? "authority follow-up needed" : "no authority follow-up";
      const due = fmtDate(f.followUpDate);
      const duePart = due ? `, follow-up ${due}` : "";
      return `${status} — ${auth}${duePart}.`;
    }
    default:
      return payload.summary || "Resolution recorded.";
  }
}

// ────────────────────────────────────────────────────────────────────
// Tone → semantic class helper for callers
// ────────────────────────────────────────────────────────────────────

/** Maps a RiskTone to semantic-token classes (no raw colors). */
export function riskToneClasses(tone: RiskTone): { container: string; text: string; dot: string } {
  switch (tone) {
    case "high":
      return {
        container: "border-destructive/40 bg-destructive/5",
        text: "text-destructive",
        dot: "bg-destructive",
      };
    case "medium":
      return {
        container: "border-warning/30 bg-warning/5",
        text: "text-warning dark:text-warning",
        dot: "bg-warning/60",
      };
    case "clear":
      return {
        container: "border-primary/30 bg-primary/5",
        text: "text-primary",
        dot: "bg-primary",
      };
    default:
      return {
        container: "border-border bg-muted/30",
        text: "text-muted-foreground",
        dot: "bg-muted-foreground/50",
      };
  }
}
